import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { NotAndroidAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { PlayLineupBox as CorePlayLineupBox } from '@sorare/core/src/components/rivals/PlayLineupBox';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { BoostYourLineup } from 'components/rivals/BoostYourLineup';
import { PlayingStatusIcon } from 'components/rivals/PlayingStatusIcon';
import { RivalsPlayerCard } from 'components/rivals/match/RivalsPlayerCard';

import { EmptyLineup } from './EmptyLineup';
import { PlayLineup } from './PlayLineup';
import { PlayLineupBox_footballRivalsGame } from './__generated__/index.graphql';

const TacticIconImg = styled.img`
  width: var(--double-and-a-half-unit);
  aspect-ratio: 1;
`;

type Props = {
  footballRivalsGame: Nullable<PlayLineupBox_footballRivalsGame>;
  draftUrl: string;
  hideBoostCta?: boolean;
};

const messages = defineMessages({
  setLineup: {
    id: 'PlayLineupBox.setLineup',
    defaultMessage: 'Set Lineup',
  },
  editLineup: {
    id: 'PlayLineupBox.editLineup',
    defaultMessage: 'Edit Lineup',
  },
});

export const PlayLineupBox = ({
  footballRivalsGame,
  draftUrl,
  hideBoostCta,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { game, myLineup } = footballRivalsGame || {};
  const { tactic } = myLineup || {};

  const hasLineup = !!myLineup;
  const { homeFormation, awayFormation } = game || {};
  const startingPlayersSlugs = [
    ...(homeFormation?.startingLineup.flat() || []),
    ...(awayFormation?.startingLineup.flat() || []),
  ].map(({ slug: playerSlug }) => playerSlug);
  const isStartingStatusAvailable =
    !!homeFormation?.startingLineup.length &&
    !!awayFormation?.startingLineup.length;

  return (
    <CorePlayLineupBox
      avatar={currentUser && <Avatar user={currentUser} />}
      cta={
        <Horizontal>
          <NotAndroidAppFeature>
            {myLineup && footballRivalsGame && !hideBoostCta && (
              <BoostYourLineup
                slug={footballRivalsGame.slug}
                myLineup={myLineup}
              />
            )}
          </NotAndroidAppFeature>
          <LinkOverlay as={Button} to={draftUrl} size="medium" color="primary">
            <FormattedMessage
              {...(hasLineup ? messages.editLineup : messages.setLineup)}
            />
          </LinkOverlay>
        </Horizontal>
      }
    >
      {hasLineup ? (
        <Vertical gap={2}>
          <PlayLineup extraInfo>
            {myLineup.appearances.map(appearance => {
              return (
                <Vertical center gap={0.5} key={appearance.id}>
                  <RivalsPlayerCard appearance={appearance} />
                  {isStartingStatusAvailable && (
                    <PlayingStatusIcon
                      isStarting={startingPlayersSlugs.includes(
                        appearance.player?.slug || ''
                      )}
                    />
                  )}
                </Vertical>
              );
            })}
          </PlayLineup>
          {tactic && (
            <SBHorizontal>
              <Horizontal gap={0.5}>
                <TacticIconImg src={tactic.iconUrl} alt="" />
                <LabelM as="span">{tactic.displayName}</LabelM>
              </Horizontal>
              <LabelM as="span" color="var(--c-neutral-600)">
                {tactic.statName}
              </LabelM>
            </SBHorizontal>
          )}
        </Vertical>
      ) : (
        <EmptyLineup />
      )}
    </CorePlayLineupBox>
  );
};

PlayLineupBox.fragments = {
  footballRivalsGame: gql`
    fragment PlayLineupBox_footballRivalsGame on FootballRivalsGame {
      slug
      game {
        id
        homeTeam {
          slug
        }
        awayTeam {
          slug
        }
        homeFormation {
          startingLineup {
            slug
          }
        }
        awayFormation {
          startingLineup {
            slug
          }
        }
      }
      myLineup {
        id
        appearances {
          id
          player {
            slug
          }
          ...RivalsPlayerCard_footballRivalsAppearance
        }
        tactic {
          slug
          displayName
          statName
          iconUrl
        }
        ...BoostYourLineUp_footballRivalsLineup
      }
    }
    ${RivalsPlayerCard.fragments.footballRivalsAppearance}
    ${BoostYourLineup.fragments.footballRivalsLineup}
  ` as TypedDocumentNode<PlayLineupBox_footballRivalsGame>,
};
